import React, { useEffect, useMemo, useState } from "react";

import { TFunction, useCalculateCoverCropMixes, useGetCropMixComposerCrops, useGetCropMixComposerScoreFactors, useTranslation } from "utils";

import { CropsFilterState } from "models";

import { Alert, Flex, Icon, PageSpin } from "components";

import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider";
import { CMC_ROUTES } from "pages/app/crop-mix-composer/CropMixComposerOverlay";
import { MixSectionHeader } from "pages/app/crop-mix-composer/components/MixSectionHeader";
import { CropsFilters } from "pages/app/crop-mix-composer/components/compositionStep/CropsFilters";
import { CropsOverviewTable } from "pages/app/crop-mix-composer/components/compositionStep/CropsOverviewTable";
import { ScoreSidePanel } from "pages/app/crop-mix-composer/components/compositionStep/ScoreSidePanel";
import { calculateAggregateMatchScore, calculateMixDistributionShares, generateGoalScores } from "pages/app/crop-mix-composer/utils/cropMixComposer";

export const AutoMixFailureMessage = ({ t, onDismiss }: { t: TFunction; onDismiss: () => void }) => (
  <Alert
    type="info"
    description={
      <Flex align="center" gap="0.5rem">
        <Icon iconName="tabler/alert-circle" />
        {t("page.composition.autoMixGeneration.failureMessage")}
      </Flex>
    }
    closable
    onClose={onDismiss}
    className="smart-mix-failure-message"
  />
);

export const MixComposition = ({ fieldId }: { fieldId?: string }) => {
  const { t } = useTranslation("cropMixComposer");

  const {
    activeCropMixId,
    selectedCrops,
    selectedGoals,
    setAggregateGoalScores,
    setAggregateMatchScore,
    setWeightShareDistribution,
    isPageLoading,
    showAutoMixFailureMessage,
    setShowAutoMixFailureMessage,
  } = useCropMixComposerContext();

  const [filters, setFilters] = useState<CropsFilterState>({ cropFamilies: [], costCategories: [] });

  const { data: cropMixComposerResponse } = useCalculateCoverCropMixes({ cropMixId: activeCropMixId });
  const { data: scoreFactors } = useGetCropMixComposerScoreFactors();
  const { data: cropsCMC } = useGetCropMixComposerCrops(activeCropMixId);

  useEffect(() => {
    if (!scoreFactors || !cropMixComposerResponse) return;

    const weightShareDistribution = calculateMixDistributionShares(selectedCrops, cropsCMC);
    setWeightShareDistribution(weightShareDistribution);
    const aggregateGoalScores = generateGoalScores(selectedGoals, weightShareDistribution, scoreFactors, selectedCrops);
    setAggregateGoalScores(aggregateGoalScores);
    const aggregateMatchScore = calculateAggregateMatchScore(selectedCrops, weightShareDistribution);
    setAggregateMatchScore(aggregateMatchScore);
  }, [
    cropMixComposerResponse,
    cropsCMC,
    scoreFactors,
    selectedCrops,
    selectedGoals,
    setAggregateGoalScores,
    setAggregateMatchScore,
    setWeightShareDistribution,
  ]);

  const filteredCropMixComposerResponse = useMemo(
    () =>
      cropMixComposerResponse?.filter(
        (crop) =>
          (filters.cropFamilies.length === 0 || filters.cropFamilies.includes(crop.crop.cropFamily)) &&
          (filters.costCategories.length === 0 || filters.costCategories.includes(crop.crop.coastCategory))
      ),
    [cropMixComposerResponse, filters]
  );

  if (isPageLoading) return <PageSpin />;

  return (
    <Flex className="crop-mix-composer-overlay-step-overview" data-testid="crop-mix-composer-overlay-step-overview">
      <Flex className="table-section" vertical gap="1rem">
        <MixSectionHeader title={t(`page.${CMC_ROUTES.COMPOSITION}.header`)} subTitle={t(`page.${CMC_ROUTES.COMPOSITION}.subHeader`)} />
        {showAutoMixFailureMessage && <AutoMixFailureMessage t={t} onDismiss={() => setShowAutoMixFailureMessage(false)} />}
        <CropsFilters cropMixComposerResponse={cropMixComposerResponse} filters={filters} setFilters={setFilters} />
        <CropsOverviewTable cropMixComposerResponse={filteredCropMixComposerResponse} />
      </Flex>
      <ScoreSidePanel fieldId={fieldId} />
    </Flex>
  );
};
